import React, { useEffect, useRef } from "react";

const RisingEmbers = ({
	width = 300,
	height = 400,
	emberInterval = 150, // Reduced frequency (from 100 to 150ms)
	initialEmbers = 10, // Reduced initial count (from 20 to 10).
	smallestEmberSize = 2,
	largestEmberSize = 5,
}) => {
	const canvasRef = useRef(null);
	const embersRef = useRef([]);
	const intervalRef = useRef(null);
	const animationRef = useRef(null);

	class Ember {
		constructor() {
			this.id = Math.random();
			this.size = Math.random() * (largestEmberSize - smallestEmberSize) + smallestEmberSize;
			this.initialX = Math.random() * width;
			this.x = this.initialX;
			this.y = height + 20;
			this.startY = this.y;
			this.riseDuration = Math.random() * 2 + 4;
			this.windDrift = Math.random() * 50 - 25; // Reduced wind drift
			this.startTime = Date.now();
			this.opacity = 1;
		}

		update() {
			const progress =
				(Date.now() - this.startTime) / (this.riseDuration * 1000);

			if (progress >= 1) return false;

			this.y = this.startY - (height + 40) * progress;
			this.x = this.initialX + this.windDrift * progress;
			this.opacity = 1 - progress * progress;

			return true;
		}

		draw(ctx) {
			ctx.beginPath();
			ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
			const gradient = ctx.createRadialGradient(
				this.x,
				this.y,
				0,
				this.x,
				this.y,
				this.size
			);
			gradient.addColorStop(0, `rgba(123, 44, 191, ${this.opacity})`);
			gradient.addColorStop(
				1,
				`rgba(243, 223, 255, ${this.opacity * 0.1})`
			);
			ctx.fillStyle = gradient;
			ctx.fill();
		}
	}

	const animate = () => {
		const ctx = canvasRef.current.getContext("2d");
		ctx.clearRect(0, 0, width, height);

		embersRef.current = embersRef.current.filter((ember) => {
			const keep = ember.update();
			if (keep) ember.draw(ctx);
			return keep;
		});

		animationRef.current = requestAnimationFrame(animate);
	};

	useEffect(() => {
		const canvas = canvasRef.current;
		canvas.width = width;
		canvas.height = height;

		embersRef.current = Array.from(
			{ length: initialEmbers },
			() => new Ember()
		);
		animate();

		intervalRef.current = setInterval(() => {
			embersRef.current.push(new Ember());
		}, emberInterval);

		const handleVisibilityChange = () => {
			if (document.hidden) {
				cancelAnimationFrame(animationRef.current);
				clearInterval(intervalRef.current);
			} else {
				animate();
				intervalRef.current = setInterval(() => {
					embersRef.current.push(new Ember());
				}, emberInterval);
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			clearInterval(intervalRef.current);
			cancelAnimationFrame(animationRef.current);
			document.removeEventListener(
				"visibilitychange",
				handleVisibilityChange
			);
		};
	}, [width, height, initialEmbers, emberInterval]);

	return (
		<canvas
			ref={canvasRef}
			style={{
				width: `100%`,
				height: `100%`,
				mixBlendMode: "screen",
			}}
		/>
	);
};

export default RisingEmbers;
